import React, { useMemo } from "react";
import { Card, CardContent, Paper, Typography } from "@mui/material";
import { useMediaQuery } from "react-responsive";

import CreativeCarouselBase from "@/features/campaigns/components/CreativeCarousel";
import RpMUIModalBase from "@/components/modals/RpMUIModal";
import { getCreativePackageLabel } from "@/features/campaigns/digital/launch-digital-campaign/helpers/creativeLabel";

type ExtendChannel = {
  id?: number | string;
  fk_channel_id?: number | string;
  attachment?: string;
  creative_name?: string;
  channel_name?: string;
};

type ExtendCreative = {
  id: number | string;
  channels?: ExtendChannel[];
  [key: string]: unknown;
};

type ExtendCreativeChannelsProps = {
  creatives: ExtendCreative[];
  viewCreative: number | string;
  previewModalOpen?: boolean;
  onClosePreviewModal?: () => void;
  location_name?: React.ReactNode;
};

const CreativeCarousel = CreativeCarouselBase as React.ComponentType<
  Record<string, unknown>
>;

const RpMUIModal = RpMUIModalBase as unknown as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

function ExtendCreativeChannels({
  creatives,
  viewCreative,
  previewModalOpen,
  onClosePreviewModal,
  location_name,
}: ExtendCreativeChannelsProps) {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 897px)" });

  const getCreativeChannels = useMemo(() => {
    const creative = creatives.find((c) => c.id === viewCreative);
    const channels = creative ? creative.channels : [];
    // Transform channels to match CreativeCarousel expected structure
    return channels.map((channel) => ({
      id: channel.fk_channel_id || channel.id,
      attachment: channel.attachment,
      creative_name: channel.creative_name,
      channel: {
        channel_name: channel.channel_name,
      },
    }));
  }, [creatives, viewCreative]);

  const selectedCreativeLabel = useMemo(() => {
    const creative = creatives?.find((c) => c.id === viewCreative);
    return getCreativePackageLabel(creative);
  }, [creatives, viewCreative]);

  return (
    <>
      <Card
        className="creative-channels creative-carousel-arrows"
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
        }}
        elevation={2}
      >
        <CardContent sx={{ textAlign: "center" }}>
          <CreativeCarousel channels={getCreativeChannels} />
        </CardContent>
      </Card>

      {previewModalOpen && isTabletOrMobile && (
        <RpMUIModal
          open={previewModalOpen}
          onClose={onClosePreviewModal}
          title="Creative Channels"
          heading={
            <>
              <Typography
                sx={{ color: "var(--neutral-700) !important" }}
                variant="h5"
                color="text.primary"
                className="text-center"
              >
                {location_name}
              </Typography>
              <Typography
                variant="body2"
                className="text-center"
                sx={{ fontWeight: 600, fontSize: "12px !important" }}
              >
                {selectedCreativeLabel}
              </Typography>
            </>
          }
        >
          <Paper className="text-center" elevation={0}>
            <Card className="creative-channels creative-carousel-arrows">
              <CardContent sx={{ textAlign: "center" }}>
                <CreativeCarousel channels={getCreativeChannels} />
              </CardContent>
            </Card>
          </Paper>
        </RpMUIModal>
      )}
    </>
  );
}

export default ExtendCreativeChannels;
